import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Copy, Check, QrCode, Printer, FileImage, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = "https://aristocratsja.com";

/**
 * ShareQrModal — premium gold-on-black QR code share modal.
 * Renders the AJA-branded QR for the production site.
 *
 * Download flow:
 *   • Uses fetch + Blob + URL.createObjectURL + temp <a download> so
 *     it works reliably on iOS Safari + Android Chrome (the simple
 *     `<a href download>` attribute is ignored on most mobile
 *     browsers).
 *   • Includes a fallback "Open image" link so users on iOS can
 *     long-press → Save Image as a last resort.
 *
 * Three quality presets:
 *   • HD Print PNG (3000 px, sharp NEAREST) — t-shirts, posters
 *   • SVG vector — infinite scale, screen-print / vinyl
 *   • Web PNG (800 px) — phones, social, email signatures
 */
export default function ShareQrModal({ open, onClose, url = SITE_URL, title = "Share Aristocrats JA" }) {
  const [style, setStyle] = useState("gold_on_black");
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(null);

  if (!open) return null;

  const buildUrl = (params) => {
    const qs = new URLSearchParams({
      url, style, logo: "1", ...params,
    });
    return `${API_BASE}/api/qr?${qs.toString()}`;
  };

  const previewSrc = buildUrl({ size: "800" });
  const openInTab = buildUrl({ size: "3000" });

  /**
   * Reliable cross-browser download. fetch → blob → object URL →
   * temp <a download> → click → cleanup. Works on iOS Safari,
   * Android Chrome, every desktop browser.
   */
  const downloadFile = async (kind, params, filename) => {
    setDownloading(kind);
    try {
      const res = await fetch(buildUrl(params));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so Safari has time to start the save
      setTimeout(() => URL.revokeObjectURL(objUrl), 1500);
      toast.success(`${filename} downloaded`);
    } catch (err) {
      toast.error("Download failed — long-press the QR image instead and tap 'Save'");
    } finally {
      setDownloading(null);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[2100] flex items-start sm:items-center justify-center bg-black/85 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto"
        onClick={onClose}
        data-testid="share-qr-modal"
      >
        <motion.div
          initial={{ y: 20, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 10, scale: 0.98, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-luxury-black border border-gold/30 rounded-lg p-5 sm:p-6 space-y-4 my-4 sm:my-8"
        >
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold flex items-center gap-1.5">
                <QrCode size={11} /> Scan to visit
              </p>
              <h3 className="font-heading text-xl sm:text-2xl text-white mt-1">{title}</h3>
              <p className="text-[11px] text-white/45 mt-1 break-all">{url}</p>
            </div>
            <button onClick={onClose} className="text-white/50 hover:text-white shrink-0 ml-2" data-testid="qr-modal-close">
              <X size={20} />
            </button>
          </div>

          {/* QR — forced 1:1 via explicit aspect-ratio inline style.
              Clickable: tap the QR to immediately download the HD
              print-ready file. The button below is a labeled
              duplicate for accessibility. */}
          <button
            type="button"
            onClick={() => downloadFile("hd", { size: "3000" }, "aristocratsja-qr-hd-3000.png")}
            disabled={downloading !== null}
            title="Tap to download HD print-ready PNG"
            className={`relative rounded-md overflow-hidden border mx-auto block group ${
              style === "gold_on_black"
                ? "border-gold/40 bg-black hover:border-gold"
                : "border-white/15 bg-white hover:border-white"
            } transition-colors`}
            style={{ width: "min(100%, 240px)", aspectRatio: "1 / 1" }}
          >
            <img
              src={previewSrc}
              alt="QR code"
              data-testid="qr-image"
              className="absolute inset-0 w-full h-full"
              loading="eager"
              style={{ imageRendering: "pixelated", objectFit: "contain" }}
            />
            {downloading === "hd" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                <Loader2 size={24} className="animate-spin text-gold" />
              </div>
            )}
            {/* Hover hint */}
            <div className="absolute bottom-1 left-1 right-1 text-[9px] uppercase tracking-wider text-gold opacity-0 group-hover:opacity-100 transition-opacity bg-black/70 py-1 rounded text-center pointer-events-none">
              Tap to download HD
            </div>
          </button>

          {/* Style switch */}
          <div className="flex items-center justify-center gap-1.5">
            <button
              data-testid="qr-style-gold"
              onClick={() => setStyle("gold_on_black")}
              className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                style === "gold_on_black"
                  ? "border-gold bg-gold/15 text-gold"
                  : "border-white/15 text-white/55 hover:text-white"
              }`}
            >Gold on black</button>
            <button
              data-testid="qr-style-print"
              onClick={() => setStyle("black_on_white")}
              className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                style === "black_on_white"
                  ? "border-white bg-white/10 text-white"
                  : "border-white/15 text-white/55 hover:text-white"
              }`}
            >Print friendly</button>
          </div>

          {/* Download buttons — all use fetch+Blob so they reliably
              save on iOS / Android, not just open the image in a new tab. */}
          <div className="space-y-2">
            <button
              data-testid="qr-download-hd"
              type="button"
              onClick={() => downloadFile("hd", { size: "3000" }, "aristocratsja-qr-hd-3000.png")}
              disabled={downloading !== null}
              className="w-full inline-flex items-center justify-center gap-2 py-3 bg-gold text-luxury-black text-xs uppercase tracking-wider rounded hover:bg-gold/90 transition-colors font-medium disabled:opacity-60"
            >
              {downloading === "hd"
                ? <><Loader2 size={14} className="animate-spin" /> Preparing HD…</>
                : <><Printer size={14} /> Download HD PNG (3000 px · shirt-ready)</>}
            </button>
            <div className="grid grid-cols-3 gap-2">
              <button
                data-testid="qr-download-svg"
                type="button"
                onClick={() => downloadFile("svg", { format: "svg" }, "aristocratsja-qr.svg")}
                disabled={downloading !== null}
                className="inline-flex items-center justify-center gap-1.5 py-2.5 border border-gold/40 text-gold hover:bg-gold/10 text-[10px] uppercase tracking-wider rounded transition-colors disabled:opacity-60"
              >
                {downloading === "svg"
                  ? <Loader2 size={11} className="animate-spin" />
                  : <FileImage size={12} />} SVG
              </button>
              <button
                data-testid="qr-download"
                type="button"
                onClick={() => downloadFile("web", { size: "800" }, "aristocratsja-qr.png")}
                disabled={downloading !== null}
                className="inline-flex items-center justify-center gap-1.5 py-2.5 border border-white/15 text-white/80 hover:text-white text-[10px] uppercase tracking-wider rounded transition-colors disabled:opacity-60"
              >
                {downloading === "web"
                  ? <Loader2 size={11} className="animate-spin" />
                  : <Download size={12} />} Web PNG
              </button>
              <button
                data-testid="qr-copy-link"
                onClick={copyLink}
                className="inline-flex items-center justify-center gap-1.5 py-2.5 border border-white/15 text-white/80 hover:text-white text-[10px] uppercase tracking-wider rounded transition-colors"
              >
                {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                {copied ? "Copied" : "Link"}
              </button>
            </div>
            {/* Fallback: open in new tab for long-press-save on iOS */}
            <a
              href={openInTab}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="qr-open-in-tab"
              className="block text-center text-[10px] uppercase tracking-wider text-white/45 hover:text-gold transition-colors pt-1 inline-flex items-center justify-center gap-1 w-full"
            >
              <ExternalLink size={10} /> Open image (long-press to save on iPhone)
            </a>
          </div>

          <p className="text-[10px] text-white/40 text-center font-body leading-relaxed">
            <span className="text-gold">HD PNG</span> — t-shirts, flyers, posters (300 DPI ready).
            <br />
            <span className="text-gold">SVG vector</span> — screen-print, vinyl, billboards.
          </p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
